import { Controller } from '@hotwired/stimulus'
import { FormItemController } from './'

export default class extends Controller<HTMLFormElement> {
  static targets = ['name', 'width', 'permissions', 'parentPermissions', 'subheading', 'headingSize']
  static outlets = ['form-item']

  declare readonly nameTarget: HTMLInputElement
  declare readonly widthTargets: HTMLInputElement[]
  declare readonly formItemOutlet: FormItemController
  declare readonly permissionsTarget: HTMLDivElement
  declare readonly hasPermissionsTarget: boolean
  declare readonly parentPermissionsTarget: HTMLDivElement
  declare readonly subheadingTarget: HTMLTextAreaElement
  declare readonly hasSubheadingTarget: boolean
  declare readonly headingSizeTargets: HTMLInputElement[]
  declare readonly hasHeadingSizeTarget: boolean
  declare readonly hasParentPermissionsTarget: boolean

  inputs: string[][]
  formHtml: string

  initialize (): void {
    // HACK: save the state only after all controllers / plugins have been initialized
    setTimeout(() => {
      this.save()
    }, 0)
  }

  prepare (hasParentNestableId: boolean): void {
    if (!this.hasPermissionsTarget && !this.hasParentPermissionsTarget) {
      return
    }

    if (hasParentNestableId) {
      if (this.hasPermissionsTarget) {
        this.permissionsTarget.classList.add('hidden')
      }
      if (this.hasParentPermissionsTarget) {
        this.parentPermissionsTarget.classList.remove('hidden')
      }
    } else {
      if (this.hasParentPermissionsTarget) {
        this.parentPermissionsTarget.classList.add('hidden')
      }
      if (this.hasPermissionsTarget) {
        this.permissionsTarget.classList.remove('hidden')
      }
    }
  }

  save (): void {
    this.saveState()
    this.updateFormItem()
  }

  cancel (): void {
    this.element.innerHTML = this.formHtml

    if (this.nameTarget.value.length === 0) {
      this.formItemOutlet.remove()
    }
  }

  private saveState (): void {
    const formData = new FormData(this.element)

    this.inputs = [...formData.entries()].map(([name, value]) => [name, String(value)])

    const cleanElement = this.element.cloneNode(true) as HTMLFormElement

    cleanElement.querySelectorAll('.ts-wrapper').forEach((wrapper) => wrapper.remove())

    cleanElement.querySelectorAll<HTMLInputElement>('input[type="radio"], input[type="checkbox"]').forEach((input) => {
      input.defaultChecked = input.checked

      if (input.checked) {
        input.setAttribute('checked', 'checked')
      } else {
        input.removeAttribute('checked')
      }
    })

    cleanElement.querySelectorAll<HTMLSelectElement>('select.tomselected').forEach((select) => {
      select.classList.remove('ts-hidden-accessible', 'tomselected')

      const selected = new Set(formData.getAll(select.name).map(String))

      for (const option of select.options) {
        option.defaultSelected = selected.has(option.value)
      }
    })

    this.formHtml = cleanElement.innerHTML
  }

  private updateFormItem (): void {
    const name = this.nameTarget.value
    const width = this.widthTargets.find((radio: HTMLInputElement) => radio.checked)?.value ?? '12'
    const subheading = this.hasSubheadingTarget ? this.subheadingTarget.value : null
    const headingSizeClass = this.hasHeadingSizeTarget
      ? (this.headingSizeTargets.find((r) => r.checked)?.dataset.headingClass ?? null)
      : null

    this.formItemOutlet.save(name, parseInt(width), this.inputs, { subheading, headingSizeClass })
  }
}
